import { Box, Stack, Typography } from "@mui/material";
import {
  CopyLinkButton,
  MANUAL_REVIEW_LINK_TARGET,
} from "components/labelling/CopyLinkButton";
import { LabellingOverlays } from "components/scrapEyeLabelling/flows/LabellingOverlays";
import { AreaMaterialMappingModal } from "components/scrapEyeLabelling/modals/AreaMaterialMappingModal";
import { AreaSelectionModal } from "components/scrapEyeLabelling/modals/AreaSelectionModal";
import { useScrapEyeMetadata } from "contexts/scrapEyeMetadata";
import dayjs, { Dayjs } from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import { useCallback, useMemo } from "react";
import {
  EventCreate,
  LabellerTimeline,
  RailcarSlotMaterialSpan,
  ReviewPeriodData,
} from "src/store/api/generatedApi";
import {
  eventDiffersFromBaseline,
  ScrapEyesEventWithReviewData,
  useManualReviewEvents,
} from "./contexts/ManualReviewEventContext";
import { SUB_MODAL_HOTKEY_SCOPES } from "./hotkeyScopes";
import { useManualReviewHotkeys } from "./ManualReviewHotkeys";
import { MANUAL_REVIEW_SIDEBAR_WIDTH } from "./ManualReviewSidebar";
import { ManualReviewVideoPlayer } from "./ManualReviewVideoPlayer";
import { PoppableScaleWeightsPanel } from "./ScaleWeightsSidebarPanel";
import {
  chainedWillVerifyPeriodIds,
  maxSequenceGapSecondsFromChunkSeconds,
} from "./utils/editScope";
import { highlightColorsByEventId } from "./utils/reviewReasons";

interface LinkedPeriodLabellingSurfaceProps {
  location: string;
  start: Dayjs;
  end: Dayjs;
  windowStart: Dayjs;
  windowEnd: Dayjs;
  reviewPeriods: ReviewPeriodData[];
  labellerTimelines: LabellerTimeline[];
  railcarSlotMaterials?: RailcarSlotMaterialSpan[];
  massEvents: EventCreate[];
  bringInWillVerifyPeriodIds: readonly number[];
  // A re-merge is in flight, so the timelines still show the merge being replaced.
  isLoading: boolean;
  getHighlightColor?: (
    event: ScrapEyesEventWithReviewData
  ) => string | null | undefined;
}

// The bring-in modal's editing surface: a manual-review player plus the full
// labelling keyboard and its overlays, all reading the neighbour period's own
// event and video contexts. Verification stays with the outer flow, so the
// verify hotkey is inert here. Must be rendered inside the modal's
// ManualReviewEventProvider, VideoControlProvider and nested HotkeysProvider.
export const LinkedPeriodLabellingSurface = ({
  location,
  start,
  end,
  windowStart,
  windowEnd,
  reviewPeriods,
  labellerTimelines,
  railcarSlotMaterials,
  massEvents,
  bringInWillVerifyPeriodIds,
  isLoading,
  getHighlightColor,
}: LinkedPeriodLabellingSurfaceProps) => {
  const { t } = useTenantTranslation();
  const { areaMaterialSnapshots, allEvents, originalEventsById } =
    useManualReviewEvents();
  const { reservation, extra_data: extraData } = useScrapEyeMetadata();
  const hasScaleWeights = extraData.includes("mass_data");
  const maxSequenceGapSeconds = maxSequenceGapSecondsFromChunkSeconds(
    reservation.chunk_seconds
  );

  const colorsByEventId = useMemo(
    () =>
      getHighlightColor
        ? highlightColorsByEventId(allEvents, getHighlightColor)
        : undefined,
    [allEvents, getHighlightColor]
  );

  // The save's own will-verify set is settled hop by hop, so on this page it cannot
  // yet count the periods the chain will only reach after confirming. Extend it with
  // those — read live from this page's paired events — so an unverified neighbour an
  // affected crossing pair reaches reads "will be verified" here.
  const willVerifyPeriodIdsWithChain = useMemo(
    () => [
      ...chainedWillVerifyPeriodIds({
        focusEvents: allEvents,
        focusOriginalEvents: [...originalEventsById.values()],
        eventDiffersFromOriginal: eventDiffersFromBaseline,
        maxSequenceGapSeconds,
        dayPeriods: reviewPeriods,
        windowStart,
        windowEnd,
        periodStart: (period) => dayjs(period.start),
        periodEnd: (period) => dayjs(period.end),
        periodId: (period) => period.id,
        isVerified: (period) => period.verified_at != null,
        willVerifyPeriodIds: bringInWillVerifyPeriodIds,
      }),
    ],
    [
      allEvents,
      originalEventsById,
      maxSequenceGapSeconds,
      reviewPeriods,
      windowStart,
      windowEnd,
      bringInWillVerifyPeriodIds,
    ]
  );

  const noVerify = useCallback(() => Promise.resolve(), []);

  const {
    pendingAreaSelection,
    clearPendingAreaSelection,
    handleAreaSelect,
    isAreaModeActive,
    isShiftHeld,
    binEntryActive,
    binBuffer,
    binEntryAvailable,
    isAreaMaterialMappingOpen,
    closeAreaMaterialMappingModal,
    reportedMaterialsAtMappingTime,
    editingSnapshot,
    currentAreaMaterialMapping,
    currentAreaMaterialLabels,
    miniMapAreaColors,
    handleAreaMaterialUpdate,
    handleSnapshotMappingUpdate,
    handleDeleteSnapshot,
    handleMappingSnapshotClick,
    editingSnapshotCurrentMapping,
  } = useManualReviewHotkeys({
    location,
    onVerifyAndNext: noVerify,
    railcarSlotMaterials,
  });

  return (
    <>
      <Box sx={{ display: "flex", gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ManualReviewVideoPlayer
            location={location}
            start={start}
            end={end}
            reviewPeriods={reviewPeriods}
            labellerTimelines={labellerTimelines}
            areaMaterialSnapshots={areaMaterialSnapshots}
            onMappingSnapshotClick={handleMappingSnapshotClick}
            selectedSnapshotTimestamp={
              editingSnapshot?.timestamp.unix() ?? null
            }
            areaColors={miniMapAreaColors}
            isEditMode
            bringInWillVerifyPeriodIds={willVerifyPeriodIdsWithChain}
            isLoading={isLoading}
            highlightColorsByEventId={colorsByEventId}
            actionButtons={
              <CopyLinkButton
                location={location}
                videoStart={start}
                linkTarget={MANUAL_REVIEW_LINK_TARGET}
              />
            }
          />
        </Box>
        {hasScaleWeights ? (
          <Stack
            spacing={1}
            sx={{ width: MANUAL_REVIEW_SIDEBAR_WIDTH, flexShrink: 0 }}
          >
            <Typography variant="h6">{t("scaleWeights")}</Typography>
            <PoppableScaleWeightsPanel
              videoStart={start}
              massEvents={massEvents}
              errorBoundaryPanel="linked-period-mass-chart"
            />
          </Stack>
        ) : null}
      </Box>

      <AreaSelectionModal
        open={pendingAreaSelection !== null}
        onClose={clearPendingAreaSelection}
        onSelectArea={handleAreaSelect}
        eventType={pendingAreaSelection?.eventType ?? "lift"}
        areaColors={miniMapAreaColors}
        disableScopes={SUB_MODAL_HOTKEY_SCOPES}
        openedOnKeyRelease={pendingAreaSelection?.openedOnKeyRelease ?? false}
        location={location}
      />

      <AreaMaterialMappingModal
        open={isAreaMaterialMappingOpen || editingSnapshot !== null}
        onClose={closeAreaMaterialMappingModal}
        currentMapping={
          editingSnapshot
            ? editingSnapshotCurrentMapping
            : currentAreaMaterialMapping
        }
        reportedMaterials={reportedMaterialsAtMappingTime}
        isLoadingReportedMaterials={isLoading}
        onUpdateMapping={
          editingSnapshot
            ? handleSnapshotMappingUpdate
            : handleAreaMaterialUpdate
        }
        snapshot={editingSnapshot ?? undefined}
        onDeleteSnapshot={handleDeleteSnapshot}
        disableScopes={SUB_MODAL_HOTKEY_SCOPES}
        location={location}
      />

      <LabellingOverlays
        isAreaModeActive={isAreaModeActive}
        isShiftHeld={isShiftHeld}
        binEntryActive={binEntryActive}
        binBuffer={binBuffer}
        binEntryAvailable={binEntryAvailable}
        areaColors={miniMapAreaColors}
        areaMaterials={currentAreaMaterialLabels}
        location={location}
      />
    </>
  );
};
